<script setup>
  import { notify } from 'notiwind'
  import { handleError } from '~/utils/error'

  const url = useRequestURL()
  const { path, query } = useRoute()
  const client = useStrapiClient()

  const props = defineProps({
    careerSlug: { required: true, type: String },
    color: { required: true, type: String },
    job: { required: true, type: Object }
  })

  const emit = defineEmits(['apply'])

  const sticky = ref(null)
  const { top } = useElementBounding(sticky)

  function getCities(job) {
    const cities = job.locations.map(loc => loc.city)
    return [...new Set(cities)]
  }

  function onCopy(e) {
    notify({ group: 'main', type: 'success', title: 'URL de l’offre copiée' })
  }

  function redirect() {
    client(`/jobs/${props.job.id}/redirect`, { method: 'PUT' }).catch(error => handleError(error))
  }
</script>

<template>
  <section class="sticky z-10 max-w-full bg-zinc-100 max-lg:top-0 lg:fixed lg:w-80">
    <div
      class="top-0 w-full p-4 pb-2 sm:px-8 sm:py-6 sm:pb-4 lg:h-[calc(100vh-160px)] lg:min-h-full lg:p-8"
      :class="{ 'max-lg:shadow': top === 0 }"
      ref="sticky"
    >
      <div class="flex h-full max-lg:flex-wrap max-lg:items-center max-lg:justify-evenly lg:flex-col lg:space-y-8">
        <NuxtLink
          class="flex space-x-4 text-left max-lg:basis-1/4 max-sm:text-sm lg:order-1 lg:-mt-2 lg:self-start"
          :to="`${path.replace('/' + job.slug, '')}${query ? '?' + Object.keys(query).join('&') : ''}`"
        >
          <Icon class="!hidden md:!block" name="arrow-turn-down-left" size="xl" type="regular" :style="{ color: color }" />
          <p class="job-link underline decoration-2 underline-offset-2">Toutes les offres</p>
        </NuxtLink>

        <div class="max-lg:basis-1/2 max-lg:text-center lg:z-10 lg:order-3">
          <NuxtLink v-if="job.isExternal()" target="_blank" :to="job.externalUrl">
            <Button
              class="border-2 bg-white !font-sans no-underline hover:shadow"
              type="button"
              @click="redirect"
              color="link"
              outline
              :style="{ borderColor: color }"
              >Je postule</Button
            >
          </NuxtLink>
          <Button
            class="border-2 bg-white !font-sans no-underline hover:shadow"
            v-else-if="job.isStatus('active') && !job.isExpired()"
            color="link"
            outline
            :style="{ borderColor: color }"
            @click="$emit('apply')"
            >Postuler
          </Button>
          <p class="text-red-500" v-else>Les candidatures sont terminées.</p>
        </div>

        <div class="flex items-end justify-end max-lg:basis-1/4 lg:bottom-8 lg:z-0 lg:order-4 lg:!mt-0 lg:flex-1">
          <div class="space-x-2">
            <Tooltip content="Partager sur LinkedIn" placement="top">
              <NuxtLink class="duration-300 hover:text-linkedin" target="_blank" :to="`https://www.linkedin.com/sharing/share-offsite/?url=${url}`">
                <Icon fixedWidth name="linkedin-in" size="lg" type="brands" />
              </NuxtLink>
            </Tooltip>
            <Tooltip content="Copier l’URL" placement="top">
              <button class="ml-2 duration-300 max-sm:text-xs" v-clipboard:copy="url" v-clipboard:success="onCopy">
                <Icon class="icon-link" fixedWidth name="link" size="lg" type="solid" />
              </button>
            </Tooltip>
          </div>
        </div>

        <div class="mt-4 w-full lg:order-2">
          <ul class="text-sm max-lg:-mx-2 max-lg:flex max-lg:flex-wrap max-lg:justify-between xs:text-base sm:text-lg lg:space-y-3">
            <li class="capitalize max-lg:px-2 max-lg:py-1" v-if="job.locations?.length">{{ getCities(job).join(', ') }}</li>
            <li class="max-lg:px-2 max-lg:py-1" v-if="job.sectors?.length">{{ job.sectors.map(s => s.name).join(', ') }}</li>
            <li class="max-lg:px-2 max-lg:py-1" v-if="job.categories?.length">
              Catégorie<template v-if="job.categories.length > 1">s</template>
              {{
                job.categories
                  .map(c => c.name)
                  .sort()
                  .join(', ')
              }}
            </li>
            <li class="max-lg:px-2 max-lg:py-1">
              <JobType v-if="job.type" :job="job" />
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
  .fa-circle-info,
  .icon-link:hover,
  .previous-link:hover {
    color: v-bind('color');
  }

  .popper > div {
    color: inherit;
  }
</style>
